import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '@/shared/helpers/cn.helper';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly end?: boolean;
}

/** Navigation latérale du panneau client. */
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Accueil', end: true },
  { to: '/chatbots', label: 'Mes chatbots' },
  { to: '/chatbot-requests', label: 'Demandes de chatbot' },
  { to: '/support-requests', label: 'Demandes support' },
];

export interface AppSidebarProps {
  /** Tiroir ouvert (mobile < lg) — la sidebar est fixe et coulisse. */
  readonly open: boolean;
  readonly onClose: () => void;
}

export function AppSidebar({ open, onClose }: AppSidebarProps) {
  return (
    <>
      {/* Backdrop mobile (< lg) : visible uniquement quand le tiroir est ouvert. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-night/60 backdrop-blur-sm transition-opacity duration-200 lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        className={cn(
          'fixed bottom-0 left-0 top-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-hairline bg-surface',
          'transition-transform duration-200 ease-out',
          'lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-60 lg:shrink-0',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-16 items-center justify-between gap-2 border-b border-hairline px-5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="grid size-8 shrink-0 place-items-center rounded-sm bg-accent text-on-accent">
              <span aria-hidden="true" className="font-display text-lg font-bold">
                m
              </span>
            </span>
            <span className="truncate font-display text-lg font-semibold text-ink">mada-talk</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le menu"
            className="grid size-10 shrink-0 place-items-center rounded-sm text-ink-muted transition-colors hover:bg-surface-3 hover:text-ink focus-visible:outline-accent lg:hidden"
          >
            <X aria-hidden="true" className="size-5" strokeWidth={1.75} />
          </button>
        </div>

        <nav aria-label="Navigation principale" className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-11 items-center rounded-sm px-3 py-2 text-body transition-colors',
                      isActive
                        ? 'bg-accent-soft font-medium text-accent'
                        : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}
